import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { GrNext, GrPrevious } from 'react-icons/gr';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa'; // Import sorting icons
import { motion } from 'framer-motion';

const Table = ({
  columns,
  data,
  totalElements,
  pageSize = 10,
  currentPage,
  onPageChange,
  renderRow,
  keyField = 'id', // Default keyField to 'id'
  isLoading = false, // Default isLoading to false
  onSortChange,
  sortConfig,
  useVirtualScroll = false, // New prop to toggle virtual scrolling
  rowHeight = 50, // Approximate height of each row in pixels
  viewportHeight = 500, // Height of the visible area in pixels
  isForSorting = true, // <-- Add this prop, default true
}) => {
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const [colWidths, setColWidths] = useState([]);
  const [scrollTop, setScrollTop] = useState(0);

  const totalPages = Math.ceil(totalElements / Number(pageSize)); // Ensure pageSize is a number
  const totalRows = data?.length;
  const totalHeight = totalRows * rowHeight;

  const visibleStartIndex = Math.floor(scrollTop / rowHeight);
  const visibleEndIndex = Math.min(
    totalRows,
    Math.ceil((scrollTop + viewportHeight) / rowHeight),
  );

  const visibleData = useVirtualScroll
    ? data.slice(visibleStartIndex, visibleEndIndex)
    : data;

  const handleScroll = () => {
    if (containerRef.current) {
      setScrollTop(containerRef.current.scrollTop);
    }
  };

  // useEffect(() => {
  //   console.log('Log Data', data);
  // }, [data]); // Debugging log to check data

  useEffect(() => {
    if (useVirtualScroll) {
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }
  }, [useVirtualScroll]);

  // Synchronize column widths for fixed header
  useEffect(() => {
    if (useVirtualScroll && headerRef.current && bodyRef.current) {
      const headerCells = headerRef.current.querySelectorAll('th');
      const bodyRow = bodyRef.current.querySelector('tr');
      if (bodyRow) {
        const bodyCells = bodyRow.querySelectorAll('td');
        const widths = [];
        for (let i = 0; i < headerCells.length; i++) {
          const headerWidth = headerCells[i]?.offsetWidth || 0;
          const bodyWidth = bodyCells[i]?.offsetWidth || 0;
          widths.push(Math.max(headerWidth, bodyWidth));
        }
        setColWidths(widths);
      }
    }
  }, [columns, data, useVirtualScroll, isLoading, viewportHeight, rowHeight]);

  const getPageNumbers = () => {
    const pages = [];
    const totalDisplayPages = 4; // Number of pages to display at the start and end
    const currentRange = 2; // Number of pages to display around the current page

    // Start pages
    for (let i = 1; i <= totalDisplayPages && i <= totalPages; i++) {
      pages.push(i);
    }

    // Current page range
    for (
      let i = Math.max(totalDisplayPages + 1, currentPage - currentRange);
      i <= Math.min(totalPages - totalDisplayPages, currentPage + currentRange);
      i++
    ) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    // End pages
    for (
      let i = Math.max(
        totalPages - totalDisplayPages + 1,
        totalDisplayPages + 1,
      );
      i <= totalPages;
      i++
    ) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    // Sort and add ellipses
    const finalPages = [];
    let prevPage = 0;
    pages.forEach((page) => {
      if (page - prevPage > 1) {
        finalPages.push('...');
      }
      finalPages.push(page);
      prevPage = page;
    });

    return finalPages;
  };

  const getValue = (obj, path) => {
    if (!path) return ''; // Return empty string if no path is provided
    const value = path.split('.').reduce((acc, part) => acc && acc[part], obj);
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return value ?? ''; // Return empty string if value is null or undefined
  };

  const handlePreviousClick = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1);
    }
  };

  const renderSkeleton = () => {
    return Array.from({ length: 10 }).map((_, index) => (
      <tr key={index} className="animate-pulse">
        {columns.map((_, colIndex) => (
          <td key={colIndex} className="py-2 px-3 border-b">
            <div className="h-4 bg-gray-300 rounded"></div>
          </td>
        ))}
      </tr>
    ));
  };

  const renderSkeletonOrMessage = () => {
    // Helper to check for "No Data Found" safely
    const isNoDataFound = () => {
      if (!Array.isArray(data)) return false;
      if (data.length !== 1) return false;
      const val =
        typeof data[0] === 'string' ? data[0].trim().toLowerCase() : '';
      return (
        val === 'no data found' || val === 'no datafound' || val === 'no data'
      );
    };

    if (isNoDataFound()) {
      return (
        <tr>
          <td
            colSpan={columns.length}
            className="py-2 px-3 border-b text-center">
            No Data Found
          </td>
        </tr>
      );
    }
    // Show skeleton if data is null or empty array
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return renderSkeleton();
    }
    return null;
  };

  const handleSort = (field) => {
    if (!isForSorting) return; // <-- Prevent sorting if not allowed
    if (field === 'ACTIONS' || field === 'STATUS' || field === 'state') return; // Skip sorting for ACTIONS column
    const newDirection =
      sortConfig?.field === field && sortConfig.direction === 'asc'
        ? 'desc'
        : 'asc'; // Toggle direction
    //console.log(`Sorting column: ${field}, Direction: ${newDirection}`); // Log column name and direction
    onSortChange({ field, direction: newDirection }); // Notify backend or parent of sorting change
    onPageChange(0); // Reset currentPage to 0 when sorting column changes
  };

  const getSortIcon = (field) => {
    if (!isForSorting) return null; // <-- Hide sort icon if not allowed
    if (field === 'ACTIONS' || field === 'STATUS' || field === 'state')
      return null; // No sorting icon for ACTIONS column
    if (sortConfig?.field === field) {
      // Dynamically show the correct icon based on the current sort direction
      return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />; // Default icon when no sorting is applied
  };

  // Helper for use in render
  const isNoDataFound = () => {
    if (!Array.isArray(data)) return false;
    if (data.length !== 1) return false;
    const val = typeof data[0] === 'string' ? data[0].trim().toLowerCase() : '';
    return (
      val === 'no data found' || val === 'no datafound' || val === 'no data'
    );
  };

  return (
    <div
      className="container mx-auto mb-5 mt-5"
      style={
        useVirtualScroll
          ? {
              height: `${viewportHeight}px`,
              overflow: 'hidden',
              position: 'relative',
            }
          : {}
      }>
      {useVirtualScroll ? (
        <>
          {/* Fixed header */}
          <div className="overflow-x-auto">
            <table
              className="min-w-full w-full bg-white border-gray-200"
              ref={headerRef}
              style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                {columns.map((_, idx) => (
                  <col
                    key={idx}
                    style={
                      colWidths[idx] ? { width: `${colWidths[idx]}px` } : {}
                    }
                  />
                ))}
              </colgroup>
              <thead className="bg-gray-200 font-400 text-left min-w-full">
                <tr>
                  {columns?.map((column, index) => (
                    <th
                      key={index}
                      className={`py-2 px-3 border-b text-base ${
                        column.header === 'ACTIONS'
                          ? 'text-center'
                          : isForSorting
                            ? 'cursor-pointer'
                            : ''
                      }`}
                      onClick={() => isForSorting && handleSort(column.field)}>
                      <span className="flex items-center">
                        {column.header}
                        {column.field && (
                          <span className="ml-2">
                            {getSortIcon(column.field)}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
          {/* Scrollable body */}
          <div
            style={{
              height: `${viewportHeight - 48}px`, // 48px is approx header height, adjust as needed
              overflowY: 'auto',
              position: 'relative',
            }}
            ref={containerRef}
            onScroll={handleScroll}>
            <div
              style={{
                height: `${totalHeight}px`,
                position: 'relative',
              }}>
              <div className="overflow-x-auto h-full">
                <table
                  className="min-w-full w-full bg-white border-gray-200"
                  ref={bodyRef}
                  style={{
                    position: 'absolute',
                    top: `${visibleStartIndex * rowHeight}px`,
                    tableLayout: 'fixed',
                    width: '100%',
                  }}>
                  <colgroup>
                    {columns.map((_, idx) => (
                      <col
                        key={idx}
                        style={
                          colWidths[idx] ? { width: `${colWidths[idx]}px` } : {}
                        }
                      />
                    ))}
                  </colgroup>
                  <motion.tbody
                    className={
                      (!data || data.length === 0 || isLoading) && !isLoading
                        ? 'text-center min-w-full font-600 text-base'
                        : ''
                    }
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}>
                    {isLoading ? (
                      renderSkeleton()
                    ) : !data || data.length === 0 || isNoDataFound() ? (
                      renderSkeletonOrMessage()
                    ) : (
                      <>
                        {visibleData.map((item, index) => (
                          <React.Fragment
                            key={
                              keyField
                                ? item[keyField]
                                : visibleStartIndex + index
                            }>
                            <motion.tr
                              className="hover:bg-slate-100"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}>
                              {columns.map((column, colIndex) => (
                                <td
                                  key={colIndex}
                                  className={`py-2 px-3 border-b font-500 text-sm ${
                                    column.header === 'ACTIONS'
                                      ? 'text-center'
                                      : ''
                                  }${
                                    useVirtualScroll
                                      ? ' whitespace-normal break-words max-w-[180px]'
                                      : ''
                                  }`}
                                  style={
                                    useVirtualScroll ? { maxWidth: 180 } : {}
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}>
                                  {column.render
                                    ? column.render(item)
                                    : column.field === 'ACTIONS'
                                      ? ''
                                      : getValue(item, column.field)}{' '}
                                </td>
                              ))}
                            </motion.tr>
                            {renderRow && (
                              <motion.tr
                                className="bg-gray-50"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}>
                                <td colSpan={columns.length}>
                                  {renderRow(item)}
                                </td>
                              </motion.tr>
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </motion.tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : (
        // ...existing code for non-virtual scroll...
        <div
          className="overflow-x-auto"
          style={
            useVirtualScroll
              ? { height: `${totalHeight}px`, position: 'relative' }
              : {}
          }>
          <table
            className="min-w-full w-full bg-white border-gray-200"
            style={
              useVirtualScroll
                ? {
                    position: 'absolute',
                    top: `${visibleStartIndex * rowHeight}px`,
                  }
                : {}
            }>
            <thead className="bg-gray-200 font-400 text-left min-w-full">
              <tr>
                {columns?.map((column, index) => (
                  <th
                    key={index}
                    className={`py-2 px-3 border-b text-base ${
                      column.header === 'ACTIONS'
                        ? 'text-center'
                        : isForSorting
                          ? 'cursor-pointer'
                          : ''
                    }`}
                    onClick={() => isForSorting && handleSort(column.field)} // Only allow sorting if enabled
                  >
                    <span className="flex items-center">
                      {column.header}
                      {column.field && (
                        <span className="ml-2">
                          {getSortIcon(column.field)}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <motion.tbody
              className={
                (!data || data.length === 0 || isLoading) && !isLoading
                  ? 'text-center min-w-full font-600 text-base'
                  : ''
              }
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}>
              {isLoading ? (
                renderSkeleton() // Show skeleton loader when loading
              ) : !data || data.length === 0 || isNoDataFound() ? (
                renderSkeletonOrMessage()
              ) : (
                <>
                  {visibleData.map((item, index) => (
                    <React.Fragment
                      key={
                        keyField ? item[keyField] : visibleStartIndex + index
                      } // Use keyField if provided
                    >
                      <motion.tr
                        className="hover:bg-slate-100"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}>
                        {columns.map((column, colIndex) => (
                          <td
                            key={colIndex}
                            className={`py-2 px-3 border-b font-500 text-sm ${
                              column.header === 'ACTIONS' ? 'text-center' : ''
                            }`}
                            style={{ whiteSpace: 'nowrap' }}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering row click events
                            }}>
                            {column.render
                              ? column.render(item) // Use custom render function if provided
                              : column.field === 'ACTIONS'
                                ? '' // Ensure ACTIONS column is empty if no render function is provided
                                : getValue(item, column.field)}{' '}
                          </td>
                        ))}
                      </motion.tr>
                      {renderRow && (
                        <motion.tr
                          className="bg-gray-50"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}>
                          <td colSpan={columns.length}>{renderRow(item)}</td>
                        </motion.tr>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </motion.tbody>
          </table>
        </div>
      )}
      {!useVirtualScroll && totalElements > 0 && (
        <div className="lg:flex lg:justify-between my-5 mx-10 sm:flex sm:justify-center items-center">
          <div className="text-sm font-600 text-gray-400 w-1/4">
            Showing {currentPage * Number(pageSize) + 1} to{' '}
            {Math.min((currentPage + 1) * Number(pageSize), totalElements)} of{' '}
            {totalElements} entries
          </div>
          <div className="flex flex-wrap items-center">
            {currentPage !== 0 && (
              <button
                onClick={handlePreviousClick}
                disabled={currentPage === 0}
                className="px-2 py-1 text-xs rounded hover:bg-gray-200 disabled:bg-gray-200 w-7 h-7">
                <GrPrevious />
              </button>
            )}
            {getPageNumbers().map((pageNumber, index) =>
              pageNumber === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-2 py-1 text-xs rounded text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={`page-${pageNumber}`}
                  className={`px-2 py-1 text-xs rounded-full mx-1 w-9 h-9 font-700 text-nowrap m-1 ${
                    currentPage === pageNumber - 1
                      ? 'btn-primary'
                      : 'bg-gray-200'
                  }`}
                  onClick={() => onPageChange(pageNumber - 1)}>
                  {pageNumber}
                </button>
              ),
            )}
            {currentPage !== totalPages - 1 && (
              <button
                onClick={handleNextClick}
                disabled={currentPage === totalPages - 1}
                className="px-2 py-1 text-xs rounded hover:bg-gray-200 disabled:bg-gray-200 w-7 h-7">
                <GrNext />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      field: PropTypes.string,
      render: PropTypes.func,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalElements: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  renderRow: PropTypes.func,
  keyField: PropTypes.string,
  isLoading: PropTypes.bool,
  onSortChange: PropTypes.func.isRequired, // Add prop type for sorting handler
  sortConfig: PropTypes.shape({
    field: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }), // Add prop type for current sorting state
  useVirtualScroll: PropTypes.bool,
  rowHeight: PropTypes.number,
  viewportHeight: PropTypes.number,
  isForSorting: PropTypes.bool, // <-- Add prop type
};

Table.defaultProps = {
  onSortChange: () => {}, // Default to a no-op function
  useVirtualScroll: false,
  isForSorting: true, // <-- Default to true
};

export default Table;
